import { motion } from "framer-motion";
import { Reveal, MaskLines, EASE } from "@/components/motion";
import { Sparkle } from "@/components/Art";

const STEPS = [
  ["Idea", "A real problem worth solving", "#17B6F4"],
  ["Engineering", "Product thinking + senior execution", "#176BFF"],
  ["Impact", "Software people actually use", "#F98A17"],
];

export function Philosophy() {
  return (
    <section className="relative py-24 sm:py-36" data-testid="philosophy-section">
      <div className="wrap">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Reveal className="eyebrow mb-6 flex items-center gap-3"><span className="h-px w-8 bg-si-cyan" />Our philosophy</Reveal>
            <MaskLines lines={["We don't just imagine", <span key="b">what's next. <span className="text-cool">We build it.</span></span>]} className="h-section" />
          </div>
          <Reveal delay={0.2} className="self-end lg:col-span-4">
            <p className="text-base leading-relaxed text-si-muted sm:text-lg">
              Some ideas become our own ventures. Others become products we build alongside founders. Every engagement starts with understanding the problem, choosing the right approach, and building something that creates real value.
            </p>
          </Reveal>
        </div>
        <div className="relative mt-20">
          <motion.div aria-hidden="true" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.6, ease: EASE }} style={{ transformOrigin: "left" }}
            className="absolute left-0 right-0 top-[27px] hidden h-[2px] bg-[linear-gradient(90deg,#17B6F4,#176BFF_50%,#F98A17)] md:block" />
          <div className="grid gap-10 md:grid-cols-3">
            {STEPS.map(([t, d, c], i) => (
              <motion.div key={t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: 0.3 + i * 0.35, duration: 0.8, ease: EASE }} className="relative flex gap-5 md:block" data-testid={`philosophy-step-${i}`}>
                <span className="relative z-10 grid h-14 w-14 shrink-0 place-items-center rounded-full border bg-si-bg" style={{ borderColor: c, boxShadow: `0 0 30px -4px ${c}` }}>
                  <Sparkle className="h-5 w-5" color={c} />
                </span>
                <div className="md:mt-8">
                  <p className="font-mono text-xs tracking-[0.2em] text-si-muted">0{i + 1}{i < 2 && <span className="md:hidden"> ↓</span>}</p>
                  <p className="mt-2 text-[clamp(2rem,4vw,3.4rem)] font-extrabold uppercase leading-none tracking-[-0.03em]">{t}</p>
                  <p className="mt-3 text-base text-si-muted">{d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const WORDS = ["Ideas into products", "Products into possibilities", "From spark to scale", "Build what comes next"];

export function Marquee() {
  const row = (
    <div className="flex shrink-0 items-center">
      {WORDS.map((w, i) => (
        <span key={w} className="flex items-center">
          <span className={`px-8 text-[clamp(2.6rem,7vw,6.5rem)] font-extrabold italic leading-none tracking-[-0.04em] ${i % 2 ? "text-transparent [-webkit-text-stroke:1.5px_#3a4b6b]" : "text-si-text"}`}>{w}</span>
          <Sparkle className="h-8 w-8 shrink-0 sm:h-12 sm:w-12" color={i % 2 ? "#17B6F4" : "#FFD166"} />
        </span>
      ))}
    </div>
  );
  return (
    <section className="marquee relative overflow-hidden border-y border-si-line bg-si-bg2 py-10 sm:py-14" aria-label="SparkIdea Interactive taglines" data-testid="editorial-marquee">
      <div className="marquee-track flex w-max">{row}<div aria-hidden="true" className="flex">{row}</div></div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-si-bg2 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-si-bg2 to-transparent" />
    </section>
  );
}
